import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, MapPinIcon, CalendarDaysIcon, UsersIcon, AdjustmentsHorizontalIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import { LAYOUT_CONFIG } from '../config/layout';
import DateRangePicker from './FloatingSearchBar/DateRangePicker';
import GuestSelector from './FloatingSearchBar/GuestSelector';
import { SearchFilters, DateRange } from '../types/SearchFilters';

const SearchBar: React.FC = () => {
  const [isCompact, setIsCompact] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const location = useLocation();
  const [filters, setFilters] = useState<SearchFilters>({
    destination: '',
    checkIn: undefined,
    checkOut: undefined,
    guests: { adults: 1, children: 0, infants: 0 },
    propertyTypes: [],
    amenities: [],
    roomTypes: []
  });
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });

  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsCompact(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  if (!['/stays', '/experiences', '/'].includes(location.pathname)) {
    return null;
  }

  return (
    <motion.div
      initial={false}
      animate={{
        height: isCompact ? LAYOUT_CONFIG.SEARCHBAR_HEIGHT_COMPACT : LAYOUT_CONFIG.SEARCHBAR_HEIGHT,
        opacity: 1
      }}
      className={`
        sticky top-20 z-40 bg-white/95 backdrop-blur-sm transition-all duration-300
        ${isCompact ? 'shadow-md' : 'border-b border-gray-200'}
      `}
      style={{
        top: LAYOUT_CONFIG.HEADER_HEIGHT,
        zIndex: LAYOUT_CONFIG.Z_INDEX.SEARCHBAR
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-full">
        <div className={`flex items-center h-full gap-4 ${isCompact ? 'py-2' : 'py-4'}`}>
          <div className="flex-1 min-w-0 relative">
            <div className="relative flex items-center">
              <MapPinIcon className="h-5 w-5 text-gray-400 absolute left-3" />
              <input
                type="text"
                placeholder="Where are you going?"
                value={filters.destination}
                onChange={(e) => setFilters(prev => ({ ...prev, destination: e.target.value }))}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full 
                         focus:ring-2 focus:ring-airbnb-red focus:border-transparent
                         transition-all hover:border-gray-300"
              />
            </div>
          </div>

          <DateRangePicker
            startDate={dateRange.startDate}
            endDate={dateRange.endDate}
            onChange={setDateRange}
            className={`transition-all duration-200 ${isCompact ? 'w-72' : 'w-96'}`}
          />

          <GuestSelector
            guests={filters.guests}
            onChange={(guests) => setFilters(prev => ({ ...prev, guests }))}
            className={`transition-all duration-200 ${isCompact ? 'w-48' : 'w-64'}`}
          />

          <button
            onClick={() => setShowFilters(!showFilters)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <AdjustmentsHorizontalIcon className="h-6 w-6 text-gray-600" />
          </button>

          <button
            onClick={() => {}}
            className="bg-airbnb-red text-white px-6 py-2 rounded-full hover:bg-red-600 
                     transition-colors flex items-center gap-2 shadow-sm hover:shadow-md
                     active:scale-95 transform duration-200"
          >
            <MagnifyingGlassIcon className="h-5 w-5" />
            {!isCompact && <span>Search</span>}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default SearchBar;